/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import divorcePartnerCheckinStyle from "./divorcePartnerCheckinStyle";
import * as loginActions from "@/src/store/login/actions";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "@/src/services/landingPagesList";
import ApiClient from "@/src/services/apiClient";
import Typography from "@mui/material/Typography";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import DivorcePartnerCheckinForm from "./DivorcePartnerCheckinForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import {Box} from "@mui/material";
import { getPartnerNameByIri } from "@/src/constants/partnerIris";
import type { PartnerIri } from "@/src/constants/partnerIris";

type DivorcePartnerCheckinProps = {
  idPrefix: string;
  color?: "default" | "grey";
  checkinHeading?: string;
  target?: string;
  partner: PartnerIri;
};

export default function DivorcePartnerCheckin({
  color = "default",
  target = "/servicewelt",
  idPrefix,
  checkinHeading = "Wir führen Sie Schritt für Schritt durch Ihre Scheidung",
  partner,
}: DivorcePartnerCheckinProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const initialValues = {
    telephone: "",
    email: "",
    gender: "",
    familyName: "",
    givenName: "",
    classification: "",
    caseReferenceNumber: "",
    divorceWanted: "yes",
  };
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const queryClient = useQueryClient();
  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);

    const emailCheck = await loginActions.checkEmail(values.email.toLowerCase());
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const partnerName = getPartnerNameByIri(partner);
    const data = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      telephone: values.telephone,
      person: {
        gender: values.gender || null,
        givenName: values.givenName,
        familyName: values.familyName,
      },
      prelead: {
        initialProduct: values.divorceWanted === "yes" ? "divorce" : "familyLawCase",
        classification: values.classification,
        landingPage: landingPage.keyword,
        partner: partnerName,
      },
    };

    const createProduct = async (divorceWanted: boolean) => {
      return await createMutation.mutateAsync({
        uri: divorceWanted ? "/divorces" : "/family_law_cases",
        data: {
          acquisitionPartner: partner,
          affiliateCode: localStorage.getItem("gclid"),
          landingPage: landingPage.keyword,
        },
      });
    };

    const insuranceData = {
      body: JSON.stringify({
        caseReferenceNumber: values.caseReferenceNumber,
      }),
    };

    await loginActions.register(data);
    const product = await createProduct(values.divorceWanted === "yes");
    await ApiClient.patch("/case_insurances/" + product.caseInsurance.id, insuranceData);
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(values.divorceWanted === "yes" ? target : "/servicewelt");
  };

  return (
    <Paper elevation={4} sx={[divorcePartnerCheckinStyle.formPaper, divorcePartnerCheckinStyle[color]]}>
      <Box id={idPrefix + "-checkin"} />
      <Typography variant={"h5"} sx={divorcePartnerCheckinStyle.heading}>
        {checkinHeading}
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <DivorcePartnerCheckinForm isLoading={isLoading} emailErrorText={emailErrorText} setEmailErrorText={setEmailErrorText} />
      </FormProvider>
    </Paper>
  );
}
